import { useState } from "react";
import { MoreHorizontal, ExternalLink, Eye, Ban, PlayCircle, CalendarPlus, Zap, RotateCcw, Flag, Trash2, KeyRound, Loader2, Eraser } from "lucide-react";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { adminApi } from "@/lib/adminApi";
import { startImpersonation } from "@/lib/impersonation";
import { toast } from "sonner";

// Quick actions for a customer row: impersonation + subscription + support tools.
export default function AdminCustomerActions({ customer, onDone }) {
  const [busy, setBusy] = useState(false);
  const uid = customer.user_id || customer.id;

  const impersonate = async (edit) => {
    try {
      const r = await adminApi.post(`/admin/impersonate/${uid}`, { edit_mode: edit });
      startImpersonation(r.data.token, edit);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not open dashboard"); }
  };

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onDone?.(); }
    catch (e) { toast.error(e.response?.data?.detail || "Action failed"); }
    finally { setBusy(false); }
  };
  const sub = (body) => adminApi.post(`/admin/customers/${uid}/subscription`, body);
  const act = (body) => adminApi.post(`/admin/customers/${uid}/action`, body);
  const support = (body) => adminApi.post(`/admin/customers/${uid}/support`, body);

  const del = () => {
    if (!window.confirm(`Permanently delete ${customer.business_name || customer.email}? This removes ALL their data and cannot be undone.`)) return;
    run(() => act({ action: "delete" }), "Account deleted");
  };

  const clearDemo = () => {
    if (!window.confirm(`Clear demo/sample data (calls, appointments, messages) for ${customer.business_name || customer.email}? Their settings and account are kept.`)) return;
    run(() => adminApi.post(`/admin/customers/${uid}/clear-demo-data`), "Demo data cleared");
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button data-testid={`customer-actions-${uid}`} onClick={(e) => e.stopPropagation()} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
        <DropdownMenuLabel>Access</DropdownMenuLabel>
        <DropdownMenuItem data-testid={`open-dashboard-${uid}`} onClick={() => impersonate(true)}><ExternalLink className="h-4 w-4 mr-2" /> Open dashboard (edit)</DropdownMenuItem>
        <DropdownMenuItem data-testid={`view-as-${uid}`} onClick={() => impersonate(false)}><Eye className="h-4 w-4 mr-2" /> View as customer</DropdownMenuItem>
        <DropdownMenuLabel>Subscription</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => run(() => sub({ action: "extend_trial", days: 7 }), "Trial extended 7 days")}><CalendarPlus className="h-4 w-4 mr-2" /> Extend trial +7d</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run(() => sub({ action: "grant_minutes", minutes: 100 }), "Granted 100 bonus minutes")}><Zap className="h-4 w-4 mr-2" /> Grant 100 minutes</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run(() => sub({ action: "reset_usage" }), "Usage reset")}><RotateCcw className="h-4 w-4 mr-2" /> Reset AI usage</DropdownMenuItem>
        <DropdownMenuLabel>Account</DropdownMenuLabel>
        {customer.suspended
          ? <DropdownMenuItem onClick={() => run(() => act({ action: "unsuspend" }), "Account unsuspended")}><PlayCircle className="h-4 w-4 mr-2" /> Unsuspend</DropdownMenuItem>
          : <DropdownMenuItem onClick={() => run(() => act({ action: "suspend" }), "Account suspended")}><Ban className="h-4 w-4 mr-2" /> Suspend</DropdownMenuItem>}
        <DropdownMenuItem onClick={() => run(() => act({ action: "flag", flagged: !customer.flagged }), customer.flagged ? "Flag removed" : "Account flagged")}><Flag className="h-4 w-4 mr-2" /> {customer.flagged ? "Unflag" : "Flag"} account</DropdownMenuItem>
        <DropdownMenuItem onClick={() => run(() => support({ action: "reset_password" }), "Password reset & emailed")}><KeyRound className="h-4 w-4 mr-2" /> Reset password</DropdownMenuItem>
        <DropdownMenuItem data-testid={`clear-demo-${uid}`} onClick={clearDemo}><Eraser className="h-4 w-4 mr-2" /> Clear demo data</DropdownMenuItem>
        <DropdownMenuItem data-testid={`delete-customer-${uid}`} onClick={del} className="text-red-600 focus:text-red-600"><Trash2 className="h-4 w-4 mr-2" /> Delete account</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
